import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Bookmark, FolderClosed, ChevronDown, Video } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  EXAM_LABELS,
  SUBJECT_LABELS,
  DIFFICULTY_LABELS,
  difficultyClass,
  subjectAccent,
} from "@/lib/pyqMeta";
import { MathText } from "@/components/MathText";
import { BookmarkButton } from "@/components/BookmarkButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const DEFAULT_FOLDERS = ["General", "Revise", "Tricky", "Formula"];

export default function Bookmarks() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [folderFilter, setFolderFilter] = useState("ALL");
  const [subjectFilter, setSubjectFilter] = useState("ALL");

  const load = () => {
    setLoading(true);
    api.get("/bookmarks").then((r) => setItems(r.data.items)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const folders = useMemo(() => {
    const s = new Set(items.map((q) => q.folder || "General"));
    return [...s].sort();
  }, [items]);

  const subjects = useMemo(() => [...new Set(items.map((q) => q.subject))], [items]);

  const folderOptions = useMemo(
    () => [...new Set([...DEFAULT_FOLDERS, ...folders])],
    [folders]
  );

  const filtered = useMemo(
    () =>
      items.filter(
        (q) =>
          (folderFilter === "ALL" || (q.folder || "General") === folderFilter) &&
          (subjectFilter === "ALL" || q.subject === subjectFilter)
      ),
    [items, folderFilter, subjectFilter]
  );

  const removeLocal = (id, on) => {
    if (!on) setItems((prev) => prev.filter((q) => q.id !== id));
  };

  const assignFolder = async (q, folder) => {
    try {
      await api.put(`/bookmarks/${q.id}/folder`, { folder });
      setItems((prev) => prev.map((x) => (x.id === q.id ? { ...x, folder } : x)));
      toast.success(`Moved to “${folder}”`);
    } catch {
      toast.error("Could not update label");
    }
  };

  const newFolder = (q) => {
    const name = window.prompt("New label name:");
    if (name && name.trim()) assignFolder(q, name.trim());
  };

  const practise = () => {
    const p = new URLSearchParams({ bookmarked: "1", mode: "UNTIMED" });
    if (folderFilter !== "ALL") p.set("folder", folderFilter);
    if (subjectFilter !== "ALL") p.set("subject", subjectFilter);
    navigate(`/student/session?${p.toString()}`);
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">Bookmarks</h1>
          <p className="mt-2 text-muted-foreground">
            Your saved questions — organise them into labels and revise before exams.
          </p>
        </div>
        <Button
          data-testid="practise-bookmarks-btn"
          onClick={practise}
          disabled={loading || filtered.length === 0}
          className="rounded-full px-6"
        >
          <Play className="mr-1.5 h-4 w-4" /> Practise ({filtered.length})
        </Button>
      </div>

      {!loading && items.length > 0 && (
        <div className="mb-6 space-y-3" data-testid="bookmark-filters">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Labels</span>
            {["ALL", ...folders].map((f) => (
              <button
                key={f}
                data-testid={`folder-filter-${f}`}
                onClick={() => setFolderFilter(f)}
                className={cn(
                  "rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
                  folderFilter === f
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-secondary text-muted-foreground hover:text-foreground"
                )}
              >
                {f === "ALL" ? "All" : f}
              </button>
            ))}
          </div>
          {subjects.length > 1 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Subject</span>
              {["ALL", ...subjects].map((s) => (
                <button
                  key={s}
                  data-testid={`subject-filter-${s}`}
                  onClick={() => setSubjectFilter(s)}
                  className={cn(
                    "rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
                    subjectFilter === s
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-secondary text-muted-foreground hover:text-foreground"
                  )}
                >
                  {s === "ALL" ? "All" : SUBJECT_LABELS[s] || s}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-28 w-full rounded-2xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-14 text-center" data-testid="bookmarks-empty">
          <Bookmark className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p className="font-heading text-lg font-semibold">
            {items.length === 0 ? "No bookmarks yet" : "Nothing in this view"}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {items.length === 0
              ? "Tap the bookmark icon on any question to save it here for later."
              : "Try a different label or subject filter."}
          </p>
          {items.length === 0 && (
            <Button asChild variant="outline" className="mt-6 rounded-full">
              <a href="/student/practice/pyq">Browse questions</a>
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4" data-testid="bookmarks-list">
          {filtered.map((q, idx) => (
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: Math.min(idx * 0.03, 0.3) }}
              data-testid={`bookmark-card-${q.id}`}
              className="group rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary/40"
            >
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="rounded-full border-border font-medium">
                  {EXAM_LABELS[q.examType] || q.examType}
                </Badge>
                <span className={cn("text-xs font-semibold uppercase tracking-wide", subjectAccent(q.subject))}>
                  {SUBJECT_LABELS[q.subject] || q.subject}
                </span>
                <Badge className={cn("rounded-full border", difficultyClass(q.difficulty))}>
                  {DIFFICULTY_LABELS[q.difficulty]}
                </Badge>
                <span className="text-xs text-muted-foreground">{q.chapter}</span>
                {q.videoEligible && (
                  <Badge data-testid={`video-badge-${q.id}`} className="rounded-full border-primary/30 bg-primary/10 text-[10px] text-primary hover:bg-primary/15">
                    <Video className="mr-1 h-3 w-3" /> Video solution
                  </Badge>
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid={`folder-chip-${q.id}`}
                      className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium text-foreground transition-colors hover:border-primary/50"
                    >
                      <FolderClosed className="h-3.5 w-3.5 text-primary" />
                      {q.folder || "General"}
                      <ChevronDown className="h-3 w-3 opacity-60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" data-testid={`folder-menu-${q.id}`}>
                    {folderOptions.map((f) => (
                      <DropdownMenuItem key={f} onClick={() => assignFolder(q, f)}>
                        <FolderClosed className="mr-2 h-4 w-4 opacity-60" /> {f}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid={`folder-new-${q.id}`} onClick={() => newFolder(q)}>
                      + New label…
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <BookmarkButton questionId={q.id} active onChange={removeLocal} size="sm" />
              </div>
              <MathText
                block
                className="line-clamp-3 text-[15px] leading-relaxed text-foreground/90"
                text={q.questionText}
              />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
